// Ported from greenline-app (TabletPOS) PR #2837. Keep in sync until it moves to @getgreenline/homi-shared.

import {
  UBER_EATS_ALERT_TIMING_LIMITS,
  UBER_EATS_DEFAULT_ALERT_TIMING,
} from '../constants/UberEats'
import { dayjs } from './dayjs'
import { IUberEatsAlertTiming } from '../models/UberEats/UberEatsModels'

type AlertTimingField = keyof IUberEatsAlertTiming
type StepDelta = 1 | -1

const { urgentMin, urgentMax, warningMax } = UBER_EATS_ALERT_TIMING_LIMITS

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const clampAlertTiming = (timing: Partial<IUberEatsAlertTiming>): IUberEatsAlertTiming => {
  const { urgentMinutes, warningMinutes } = timing
  if (!Number.isFinite(urgentMinutes) || !Number.isFinite(warningMinutes)) {
    return UBER_EATS_DEFAULT_ALERT_TIMING
  }

  const urgent = clamp(Math.round(urgentMinutes as number), urgentMin, urgentMax)
  const warning = clamp(Math.round(warningMinutes as number), urgent + 1, warningMax)
  return { urgentMinutes: urgent, warningMinutes: warning }
}

export const parseAlertTiming = (raw: string | null): IUberEatsAlertTiming => {
  if (!raw) return UBER_EATS_DEFAULT_ALERT_TIMING

  try {
    return clampAlertTiming(JSON.parse(raw))
  } catch {
    return UBER_EATS_DEFAULT_ALERT_TIMING
  }
}

export const canDecrementUrgent = (t: IUberEatsAlertTiming) => t.urgentMinutes > urgentMin
export const canIncrementUrgent = (t: IUberEatsAlertTiming) =>
  t.urgentMinutes < Math.min(urgentMax, t.warningMinutes - 1)
export const canDecrementWarning = (t: IUberEatsAlertTiming) =>
  t.warningMinutes > t.urgentMinutes + 1
export const canIncrementWarning = (t: IUberEatsAlertTiming) => t.warningMinutes < warningMax

const STEP_GUARDS: Record<
  AlertTimingField,
  Record<StepDelta, (t: IUberEatsAlertTiming) => boolean>
> = {
  urgentMinutes: { '-1': canDecrementUrgent, '1': canIncrementUrgent },
  warningMinutes: { '-1': canDecrementWarning, '1': canIncrementWarning },
}

export const stepAlertTiming = (
  timing: IUberEatsAlertTiming,
  field: AlertTimingField,
  delta: StepDelta,
): IUberEatsAlertTiming => {
  if (!STEP_GUARDS[field][delta](timing)) return timing
  return { ...timing, [field]: timing[field] + delta }
}

export const formatAlertTimingSummary = ({ urgentMinutes, warningMinutes }: IUberEatsAlertTiming) =>
  `Urgent: ≤${urgentMinutes} min left · Warning: ${urgentMinutes}–${warningMinutes} min left · Normal: ${warningMinutes}+ min left`

export const resolveAlertTiming = (isAlertDevice: boolean, raw: string | null) =>
  isAlertDevice ? parseAlertTiming(raw) : UBER_EATS_DEFAULT_ALERT_TIMING

export const formatPauseUntil = (until: number, now = Date.now()) => {
  const time = dayjs(until).format('h:mm A')
  if (dayjs(until).isSame(now, 'day')) return time
  if (dayjs(until).isSame(dayjs(now).add(1, 'day'), 'day')) return `tomorrow, ${time}`
  return dayjs(until).format('ddd, MMM D, h:mm A')
}
